/**
 * Warming recordings before they are played.
 *
 * Recordings are ~82 MB and served from R2 through Cloudflare's cache. The
 * expensive part of pressing play is not the file, it is the cold start:
 * DNS, TLS, and an edge cache miss that has to reach R2. A ranged request
 * for the first few hundred kilobytes pays that cost ahead of time and
 * leaves the edge entry warm.
 *
 * Two budgets, because the triggers mean different things:
 *
 * - **In view** — plausible interest. 1 KB, enough to open the connection
 *   and populate the cache entry, cheap enough to do for every visible card.
 * - **Hover** — likely intent. ~384 KB, roughly 24 seconds at 128 kbps, so
 *   playback starts from buffered audio.
 *
 * A caveat worth knowing: browsers do not reliably reuse a `fetch()` range
 * response for a later `<audio>` request (Safari especially). The dependable
 * win is the warm edge cache, which turns a cold R2 read into an edge hit
 * for everyone. Treat browser-side reuse as a bonus.
 */

const VIEWPORT_BYTES = 1024;
const HOVER_BYTES = 384 * 1024;

/** Hovering while moving the pointer across a grid is not intent. */
export const HOVER_DELAY_MS = 120;

/**
 * URLs already warmed at each budget. Scrolling back over a card should not
 * refetch, and a hover after a viewport warm should still upgrade to the
 * larger range.
 */
const warmed = new Map<string, number>();

const warm = (url: string, bytes: number, signal?: AbortSignal) => {
  if ((warmed.get(url) ?? 0) >= bytes) {
    return;
  }
  warmed.set(url, bytes);

  // Failures are deliberately ignored: this is an optimisation, and an
  // aborted or failed warm must never surface to the visitor.
  const attempt = async () => {
    try {
      await fetch(url, {
        headers: { Range: `bytes=0-${bytes - 1}` },
        signal,
      });
    } catch {
      // A failed warm should not block a later attempt at the same range.
      warmed.delete(url);
    }
  };
  void attempt();
};

export const warmForViewport = (url: string) => {
  warm(url, VIEWPORT_BYTES);
};

export const warmForHover = (url: string, signal?: AbortSignal) => {
  warm(url, HOVER_BYTES, signal);
};
